'use client';

import React from 'react';
import { Slide, SliderProvider, SliderTrack } from '@faceless-ui/slider';
import { useWindowInfo } from '@faceless-ui/window-info';
import type { ExtractLayoutType, SharedBlockType } from '../types';
import type { ArticlesQuery, PagesQuery } from '../../graphql/generated/schema';
import { BlockContainer } from '../../components/layouts';
import styles from './styles.module.scss';
import { ArticleCard, Button } from '../../components';
import { constructClassName } from '../../utils/constructClassName';
import queries from '../../styles/queries/index.module.scss';

export const ArticlesScrollerClient: React.FC<SharedBlockType & ExtractLayoutType<PagesQuery, 'ArticlesScroller'> & {
  articleDocs?: ArticlesQuery['Articles']
}> = ({ articles, articleDocs, blockSettings }) => {
  const windowInfo = useWindowInfo();

  const slidesToShow = (() => {
    switch (true) {
      case (windowInfo.width || 0) >= parseInt(queries.lg, 10):
        return 4;
      case (windowInfo.width || 0) >= parseInt(queries.sm, 10):
        return 2;
      case (windowInfo.width || 0) >= parseInt(queries.xxs, 10):
        return 1;
      default:
        return 1;
    }
  })();

  if (!articles) return null;

  return (
    <BlockContainer
      className={styles.container}
      blockSettings={blockSettings}
    >
      <div className={styles.sliderTrackOuterContainer}>
        <SliderProvider
          slidesToShow={slidesToShow}
        >
          {({ currentSlideIndex, goToSlideIndex, slidesToShow: currentSlidesToShow }) => (
            <React.Fragment>
              <SliderTrack className={styles.sliderTrackContainer}>
                {articleDocs?.docs?.map((article, index) => (
                  <Slide index={index}>
                    <div className={styles.cardContainer}>
                      <ArticleCard
                        article={article}
                      />
                    </div>
                  </Slide>
                ))}
              </SliderTrack>
              <div className={styles.dotNavigationContainer}>
                {
                  Array.from({ length: Math.ceil((articleDocs?.docs?.length || 0) / currentSlidesToShow) }).map((_, index) => (
                    <Button
                      aria-label={`Go to slide ${index * currentSlidesToShow + 1}`}
                      key={index}
                      type="tertiary"
                      className={constructClassName([
                        styles.dot,
                        currentSlideIndex >= index * currentSlidesToShow && currentSlideIndex < (index + 1) * currentSlidesToShow && styles.activeDot,
                      ])}
                      onClick={() => goToSlideIndex(index * currentSlidesToShow)}
                    />
                  ))
                }
              </div>
            </React.Fragment>
          )}
        </SliderProvider>
      </div>
    </BlockContainer>
  );
};
